import _ from 'lodash'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { Link, useLocation, useParams } from 'react-router-dom'
import { Star } from 'lucide-react'
import { Footer } from '@/components/layout/footer'
import { CasinoGameSection } from '@/components/casino/lobby/CasinoGameSection'
import { CasinoPlayActionBar, type CasinoPlayMode } from '@/components/casino/play/CasinoPlayActionBar'
import { CasinoPlayDetails } from '@/components/casino/play/CasinoPlayDetails'
import { CasinoPlayHero } from '@/components/casino/play/CasinoPlayHero'
import { PlayShell } from '@/components/play/PlayShell'
import { BetsCycleLoader } from '@/components/ui/betscycle-loader'
import { BlackjackIcon } from '@/components/casino/icons'
import { CASINO_PLAY_GAMES_PER_CAROUSEL } from '@/config/casino'
import { useDefaultCurrencyCode } from '@/hooks/use-default-currency'
import { usePlayFullscreen } from '@/hooks/use-play-fullscreen'
import { useAuthModal } from '@/context/AuthModalContext'
import { useGetCasinoGamesQuery, useLazyLaunchCasinoDemoQuery, useLazyLaunchCasinoGameQuery } from '@/store/api/casinoApi'
import { useAppDispatch, useAppSelector } from '@/store/hooks'
import { setActiveSection } from '@/store/slices/configSlice'
import { selectIsAuthenticated } from '@/store/slices/authSlice'
import { selectPlayGame } from '@/store/slices/casinoSlice'
import type { CasinoGame } from '@/types/casino'

interface CasinoPlayLocationState {
	game?: CasinoGame
}

export default function CasinoPlay() {
	const dispatch = useAppDispatch()
	const { casinoId } = useParams<{ casinoId: string }>()
	const location = useLocation()
	const locationState = location.state as CasinoPlayLocationState | null
	const storedGame = useAppSelector(selectPlayGame)
	const isAuthenticated = useAppSelector(selectIsAuthenticated)
	const { openLogin } = useAuthModal()
	const defaultCurrencyCode = useDefaultCurrencyCode()

	const [playMode, setPlayMode] = useState<CasinoPlayMode | null>(null)
	const [gameUrl, setGameUrl] = useState<string | null>(null)
	const [isTheaterMode, setIsTheaterMode] = useState(false)

	const [launchRealGame, { isFetching: isLaunchingReal }] = useLazyLaunchCasinoGameQuery()
	const [launchDemoGame, { isFetching: isLaunchingDemo }] = useLazyLaunchCasinoDemoQuery()

	const gameFromState = locationState?.game ?? storedGame
	const shouldFetchGame = !gameFromState || gameFromState.id !== casinoId

	const {
		data: fetchedGameResponse,
		isLoading: isGameLoading,
		isError: isGameError,
	} = useGetCasinoGamesQuery({ casino_id: casinoId ?? '', page: 1, per_page: 1 }, { skip: !casinoId || !shouldFetchGame })

	const game = useMemo(() => {
		if (gameFromState && gameFromState.id === casinoId) {
			return gameFromState
		}

		return _.find(fetchedGameResponse?.data, { id: casinoId }) ?? fetchedGameResponse?.data?.[0] ?? null
	}, [casinoId, fetchedGameResponse?.data, gameFromState])

	const { heroRef, handleFullscreen } = usePlayFullscreen()

	const providerId = game?.provider_id

	const {
		data: providerGamesResponse,
		isLoading: isProviderGamesLoading,
		isError: isProviderGamesError,
	} = useGetCasinoGamesQuery(
		{
			provider_id: providerId,
			page: 1,
			per_page: CASINO_PLAY_GAMES_PER_CAROUSEL + 1,
		},
		{ skip: !providerId },
	)

	const {
		data: popularGamesResponse,
		isLoading: isPopularGamesLoading,
		isError: isPopularGamesError,
	} = useGetCasinoGamesQuery({
		is_popular: true,
		page: 1,
		per_page: CASINO_PLAY_GAMES_PER_CAROUSEL,
	})

	const providerGames = useMemo(() => _.reject(providerGamesResponse?.data ?? [], { id: game?.id }), [game?.id, providerGamesResponse?.data])
	const popularGames = popularGamesResponse?.data ?? []
	const isLaunching = isLaunchingReal || isLaunchingDemo

	useEffect(() => {
		dispatch(setActiveSection('casino'))
	}, [dispatch])

	useEffect(() => {
		setGameUrl(null)
		setPlayMode(null)
		setIsTheaterMode(false)
	}, [casinoId])

	useEffect(() => {
		if (!game?.id) {
			return
		}

		const frame = window.requestAnimationFrame(() => {
			window.scrollTo({ top: 0, behavior: 'smooth' })
		})

		return () => window.cancelAnimationFrame(frame)
	}, [game?.id])

	const launchGame = useCallback(
		async (mode: CasinoPlayMode) => {
			if (!game || !casinoId) {
				return
			}

			if (mode === 'real' && !isAuthenticated) {
				openLogin()
				return
			}

			const launch = mode === 'real' ? launchRealGame : launchDemoGame
			const response = await launch({
				casino_id: casinoId,
				currency_code: defaultCurrencyCode,
			})

			if ('data' in response && response.data?.data?.url) {
				setGameUrl(response.data.data.url)
				setPlayMode(mode)
			}
		},
		[casinoId, defaultCurrencyCode, game, isAuthenticated, launchDemoGame, launchRealGame, openLogin],
	)

	const handleRealPlay = () => {
		import.meta.env.PROD ? void launchGame('real') : void launchGame('demo') // In development, we use demo mode for real play to avoid real money transactions
		// void launchGame('real') // In production, you would use the following line instead
	}

	const handleDemoPlay = () => {
		void launchGame('demo')
	}

	const handleModeChange = (mode: CasinoPlayMode) => {
		if (playMode === mode && gameUrl) {
			return
		}

		void launchGame(mode)
	}

	if (!casinoId) {
		return (
			<PlayShell>
				<p className='text-sm text-muted-foreground'>Game not found.</p>
				<Link to='/casino' className='text-sm font-medium text-primary hover:underline'>
					Back to Casino
				</Link>
			</PlayShell>
		)
	}

	if (shouldFetchGame && isGameLoading) {
		return (
			<PlayShell className='min-h-[50vh] items-center justify-center'>
				<BetsCycleLoader size={40} aria-label='Loading casino game' />
			</PlayShell>
		)
	}

	if (!game || isGameError) {
		return (
			<PlayShell>
				<p className='text-sm text-muted-foreground'>Unable to load this casino game right now.</p>
				<Link to='/casino' className='text-sm font-medium text-primary hover:underline'>
					Back to Casino
				</Link>
			</PlayShell>
		)
	}

	const providerName = game.provider?.provider_name ?? 'Provider'

	return (
		<PlayShell footer={<Footer />}>
			<CasinoPlayHero
				key={game.id}
				ref={heroRef}
				game={game}
				gameUrl={gameUrl}
				isLaunching={isLaunching}
				isTheaterMode={isTheaterMode}
				onRealPlay={handleRealPlay}
				onDemoPlay={handleDemoPlay}
			/>

			<CasinoPlayActionBar
				gameId={game.id}
				playMode={playMode}
				onModeChange={handleModeChange}
				onFullscreen={handleFullscreen}
				onTheaterToggle={() => setIsTheaterMode((current) => !current)}
				isTheaterMode={isTheaterMode}
			/>

			<CasinoPlayDetails game={game} />

			<CasinoGameSection
				title={`The Best Games by ${providerName}`}
				icon={<BlackjackIcon className='h-7 w-7 text-subtle-foreground' />}
				games={_.take(providerGames, CASINO_PLAY_GAMES_PER_CAROUSEL)}
				isLoading={isProviderGamesLoading}
				isError={isProviderGamesError}
				emptyMessage={`No other games from ${providerName} right now.`}
			/>

			<CasinoGameSection
				title='The Most Popular Games'
				icon={<Star className='h-7 w-7 text-subtle-foreground' />}
				games={popularGames}
				isLoading={isPopularGamesLoading}
				isError={isPopularGamesError}
				emptyMessage='No popular games available right now.'
			/>
		</PlayShell>
	)
}
